import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import memoizeOne from 'memoize-one';
import { getVisibleTimeByFormat } from '@elliemae/ds-utilities/timeUtils';
import { aggregatedClasses, convertPropToCssClassName } from '@elliemae/ds-classnames';
import DSInputGroup from '@elliemae/ds-form/InputGroup';
import { TimeInputImpl } from '@elliemae/ds-form/TimeInput';
import DSInput from '@elliemae/ds-form/Input';
import { TimePickerMenu } from './TimePickerMenu.js';
import TimePickerDropdown from './TimePickerDropdown.js';
import { convertTimeString } from './utils.js';
import '@babel/runtime/helpers/esm/classCallCheck';
import '@babel/runtime/helpers/esm/createClass';
import '@babel/runtime/helpers/esm/assertThisInitialized';
import '@babel/runtime/helpers/esm/inherits';
import '@babel/runtime/helpers/esm/possibleConstructorReturn';
import '@babel/runtime/helpers/esm/getPrototypeOf';
import '@babel/runtime/helpers/esm/defineProperty';
import '@elliemae/ds-utilities/utils';
import './PickerPanel.js';
import '@babel/runtime/helpers/esm/toArray';
import 'lodash';
import '@elliemae/ds-icons/ChevronSmallUp';
import '@elliemae/ds-icons/ChevronSmallDown';
import '@elliemae/ds-button';
import './moveElementOnScroll.js';
import '@elliemae/ds-icons/RecentDocuments';
import '@elliemae/ds-popper';

var blockName = 'time-picker';
var TimePickerContainer = aggregatedClasses(DSInputGroup)(blockName, null, function (_ref) {
  var isMenuOpened = _ref.isMenuOpened;
  return {
    isMenuOpened: isMenuOpened
  };
});

var TimePickerImpl = function TimePickerImpl(_ref2) {
  var value = _ref2.value,
      onChange = _ref2.onChange,
      onOpen = _ref2.onOpen,
      format = _ref2.format,
      disabled = _ref2.disabled,
      hasHeader = _ref2.hasHeader,
      hasHeaderDisplay = _ref2.hasHeaderDisplay,
      className = _ref2.className,
      containerProps = _ref2.containerProps,
      placement = _ref2.placement,
      zIndex = _ref2.zIndex,
      disabledTimes = _ref2.disabledTimes,
      minutesInterval = _ref2.minutesInterval;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      time = _useState2[0],
      setTime = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isMenuOpened = _useState4[0],
      setIsMenuOpened = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      focusedColumn = _useState6[0],
      setFocusedColumn = _useState6[1];

  var timePickerRef = useRef();
  useEffect(function () {
    if (moment(value).isValid()) setTime(moment(value));else if (value === null) setTime(undefined);
  }, [value]);
  useEffect(function () {
    window.addEventListener('click', handleClickOutside, false);
    return function () {
      return window.removeEventListener('click', handleClickOutside, false);
    };
  }, []);
  useEffect(function () {
    window.addEventListener('keydown', handleKeyDown, false);
    return function () {
      return window.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);
  useEffect(function () {
    if (isMenuOpened) handleChangeFocus();
  }, [isMenuOpened]);

  var handleClickOutside = function handleClickOutside() {
    if (isMenuOpened) setIsMenuOpened(false);
  };

  var handleKeyDown = function handleKeyDown(event) {
    var key = event.key;
    var menuOpenKeys = ['Escape', ' ', 'Enter'];
    if (menuOpenKeys.includes(key)) setIsMenuOpened(false);
  };

  var handleChangeFocus = function handleChangeFocus() {
    var _document$getElements = document.getElementsByClassName('em-ds-time-picker-panel'),
        _document$getElements2 = _slicedToArray(_document$getElements, 1),
        firstColumn = _document$getElements2[0];

    if (firstColumn && !focusedColumn) {
      setFocusedColumn(firstColumn);
      firstColumn.focus();
    }
  };

  var handlePickerChange = function handlePickerChange(newTime) {
    setTime(moment(newTime));
    onChange(moment(newTime));
  };

  var handleInputChange = function handleInputChange(newTime) {
    setTime(newTime);
    onChange(newTime);
  };

  var handleMenuOpen = function handleMenuOpen(isOpen) {
    onOpen(isOpen);
    setIsMenuOpened(isOpen);
    if (!isOpen) setFocusedColumn(null);
  };

  var getVisibleTimePanels = memoizeOne(function (format) {
    return getVisibleTimeByFormat(format);
  });
  var timePickerBlockName = convertPropToCssClassName(blockName);
  var visiblePanels = getVisibleTimePanels(format);
  var disabledTimeRange = disabledTimes && {
    from: convertTimeString(disabledTimes.from),
    to: convertTimeString(disabledTimes.to)
  };
  return /*#__PURE__*/React.createElement(TimePickerContainer, {
    className: "".concat(className, " variant-").concat(!disabled ? 'default' : 'disabled'),
    containerProps: containerProps,
    disabled: disabled,
    rightAddon: /*#__PURE__*/React.createElement(TimePickerDropdown, {
      blockName: timePickerBlockName,
      disabled: disabled,
      isOpen: isMenuOpened,
      zIndex: zIndex,
      placement: placement,
      onOpen: handleMenuOpen,
      onClick: function onClick(e) {
        e.preventDefault();
        handleMenuOpen(!isMenuOpened);
      },
      menu: /*#__PURE__*/React.createElement("div", {
        ref: timePickerRef
      }, /*#__PURE__*/React.createElement(TimePickerMenu, _extends({
        disabled: disabled,
        disabledTimes: disabledTimeRange,
        format: format,
        hasHeader: hasHeader,
        hasHeaderDisplay: hasHeaderDisplay,
        onTimeChange: handlePickerChange,
        minutesInterval: minutesInterval,
        time: time
      }, visiblePanels)))
    })
  }, /*#__PURE__*/React.createElement(DSInput, {
    customInputType: function customInputType(_ref3) {
      var handleChange = _ref3.onChange,
          restInputProps = _objectWithoutProperties(_ref3, ["onChange"]);

      return /*#__PURE__*/React.createElement(TimeInputImpl, _extends({
        clearable: true,
        disabled: disabled,
        format: format,
        onChange: handleChange
      }, restInputProps));
    },
    isShowElipsisActive: false,
    disabled: disabled,
    onChange: handleInputChange,
    value: time
  }));
};

TimePickerImpl.propTypes = {
  value: PropTypes.instanceOf(Date, moment),
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  format: PropTypes.string,
  disabled: PropTypes.bool,
  hasHeader: PropTypes.bool,
  hasHeaderDisplay: PropTypes.bool,
  className: PropTypes.string,
  containerProps: PropTypes.object,
  placement: PropTypes.string,
  zIndex: PropTypes.number,
  disabledTimes: PropTypes.object,
  minutesInterval: PropTypes.number
};

export default TimePickerImpl;
//# sourceMappingURL=TimePickerImpl.js.map
